// BHP-1070: routes the signed-in account after the Backend2.0 bootstrap. A
// freshly provisioned account without a customer (empty customerUuids) is sent
// to onboarding instead of a broken dashboard; an account with a customer falls
// through to the app.
//
// BHP-1073: a *failed* bootstrap is kept distinct from a *successful* empty one.
// Treating a query error as "no account" sent users who already have a customer
// into onboarding whenever the bootstrap hiccuped (token refresh, 5xx, or the
// slow/timeout local calls from BHP-1072).
import React from 'react';
import Loading from '../../../pages/Screens/Loading';
import { useCurrentAccount } from '../application/useCurrentAccount';
import { needsOnboarding } from '../domain/onboarding';
import AccountBootstrapError from './AccountBootstrapError';

interface Props {
  /** Rendered when the account has no customer yet (onboarding flow). */
  onboarding: React.ReactNode;
  children: React.ReactNode;
}

export default function OnboardingGate({
  onboarding,
  children
}: Props): JSX.Element {
  const { data: account, isPending, isError, refetch } = useCurrentAccount();

  if (isPending) {
    return <Loading />;
  }

  // A failed bootstrap is not an onboarding signal — offer a retry so a
  // transient error never masquerades as a customerless account.
  if (isError) {
    return (
      <AccountBootstrapError
        onRetry={() => {
          void refetch();
        }}
      />
    );
  }

  // A *successful* empty result — a null account, or an account whose
  // customerUuids is empty — is a genuine freshly-provisioned sign-up: onboard
  // it instead of dropping it on a broken dashboard.
  if (account == null || needsOnboarding(account)) {
    return <>{onboarding}</>;
  }

  return <>{children}</>;
}
